/* assets/provider-commercial.js — GCITYPARTNERS-PROVIDER-COMMERCIAL-02
 * Canonical Provider Portal commercial child runtime for directed RFQs, quotes and verified award value.
 * Browser identity comes only from LittleGWebSession native bearer; Provider approval is resolved server-side.
 * This runtime never sends a Provider id and never uses Shopify customer/App Proxy identity.
 */
(function (window, document) {
  'use strict';

  var VERSION = 'GCITYPARTNERS-PROVIDER-COMMERCIAL-02';
  var GLOBAL_KEY = '__GCITYPARTNERS_PROVIDER_COMMERCIAL__';
  var ROOT_SELECTOR = '[data-provider-commercial]';
  var API_ORIGIN = 'https://api.glhksolution.com';
  var API_PREFIX = '/v1/gcitypartners/';
  var mounted = new WeakSet();

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].mountAll === 'function') {
    window[GLOBAL_KEY].mountAll(document);
    return;
  }

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function isZh() { return clean(document.documentElement.lang).toLowerCase().indexOf('zh') === 0; }
  function words(zh, en) { return isZh() ? zh : en; }

  function endpoint(root, name) {
    var raw = clean(root.getAttribute(name));
    if (!raw) return '';
    try {
      var url = new URL(raw, API_ORIGIN);
      return url.origin === API_ORIGIN && url.pathname.indexOf(API_PREFIX) === 0 ? url.toString() : '';
    } catch (_error) { return ''; }
  }

  function nativeSession() {
    var session = window.LittleGWebSession;
    return session && typeof session.authenticatedFetch === 'function' ? session : null;
  }

  function portalAllowed() {
    var session = nativeSession();
    if (!session || typeof session.getState !== 'function') return false;
    var state = session.getState();
    return Boolean(state && state.status === 'authenticated' && state.profile && clean(state.profile.id));
  }

  async function requestJson(url, options) {
    if (!url) throw new Error('missing_endpoint');
    var session = nativeSession();
    if (!session) throw new Error('native_session_unavailable');
    var init = Object.assign({ headers: { Accept: 'application/json' } }, options || {});
    if (init.body && typeof init.body !== 'string') {
      init.body = JSON.stringify(init.body);
      init.headers = Object.assign({}, init.headers, { 'Content-Type': 'application/json' });
    }
    var response = await session.authenticatedFetch(url, init);
    var payload = await response.json().catch(function () { return {}; });
    if (!response.ok || !payload || payload.ok !== true) {
      var error = new Error(clean(payload && payload.error) || clean(payload && payload.message) || 'request_failed');
      error.status = response.status;
      error.serverMessage = clean(payload && payload.message);
      throw error;
    }
    return payload;
  }

  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }

  function setText(root, selector, value) {
    var node = root.querySelector(selector);
    if (node) node.textContent = value;
  }

  function currencyDigits(currency) {
    try { return new Intl.NumberFormat('en', { style: 'currency', currency: currency }).resolvedOptions().maximumFractionDigits; }
    catch (_error) { return 2; }
  }

  function minorValue(value) {
    if (value === null || value === undefined || clean(value) === '') return null;
    var amount = Number(value);
    return Number.isSafeInteger(amount) && amount >= 0 ? amount : null;
  }

  function money(currency, minor) {
    var amount = minorValue(minor);
    if (amount === null) return '—';
    var digits = currencyDigits(currency);
    var major = amount / Math.pow(10, digits);
    try { return new Intl.NumberFormat(isZh() ? 'zh-HK' : 'en-HK', { style: 'currency', currency: currency, currencyDisplay: 'code' }).format(major); }
    catch (_error) { return currency + ' ' + String(major); }
  }

  function majorToMinor(raw, currency) {
    var value = clean(raw).replace(/,/g, '');
    var digits = currencyDigits(currency);
    var pattern = digits > 0 ? new RegExp('^\\d{1,15}(?:\\.\\d{0,' + digits + '})?$') : /^\d{1,15}$/;
    if (!pattern.test(value)) throw new Error('invalid_amount');
    var parts = value.split('.');
    var whole = parts[0] || '0';
    var fraction = (parts[1] || '').padEnd(digits, '0');
    var minor = BigInt(whole) * (BigInt(10) ** BigInt(digits)) + BigInt(fraction || '0');
    if (minor <= 0n || minor > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('invalid_amount');
    return Number(minor);
  }

  function isoFutureFromLocal(value) {
    var raw = clean(value);
    if (!raw) return null;
    var date = new Date(raw);
    if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) throw new Error('invalid_valid_until');
    return date.toISOString();
  }

  function formatDate(value) {
    var raw = clean(value);
    if (!raw) return '';
    var date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    try { return new Intl.DateTimeFormat(isZh() ? 'zh-HK' : 'en-HK', { year: 'numeric', month: 'short', day: 'numeric' }).format(date); }
    catch (_error) { return raw; }
  }

  function scopeText(scope) {
    if (!scope || typeof scope !== 'object' || Array.isArray(scope)) return '';
    var candidates = [scope.summary, scope.description, scope.requirement, scope.title];
    for (var i = 0; i < candidates.length; i += 1) if (clean(candidates[i])) return clean(candidates[i]).slice(0, 320);
    return '';
  }

  function renderSummary(root, summary) {
    const data = summary && typeof summary === 'object'
      && !Array.isArray(summary) ? summary : null;

    function validCount(value) {
      return typeof value === 'number'
        && Number.isSafeInteger(value)
        && value >= 0;
    }

    const counts = data ? [
      data.open_rfq_count,
      data.submitted_quote_count,
      data.awarded_project_count
    ] : [];

    const values = data && Array.isArray(data.awarded_value_by_currency)
      ? data.awarded_value_by_currency : null;

    const validAmounts = values !== null && values.every(function (item) {
      return item && typeof item === 'object'
        && /^[A-Z]{3}$/.test(clean(item.currency).toUpperCase())
        && typeof item.awarded_value_minor === 'number'
        && Number.isSafeInteger(item.awarded_value_minor)
        && item.awarded_value_minor >= 0;
    });

    const valid = counts.length === 3
      && counts.every(validCount)
      && validAmounts;

    const selectors = [
      '[data-provider-commercial-metric="rfqs"]',
      '[data-provider-commercial-metric="quotes"]',
      '[data-provider-commercial-metric="awards"]'
    ];

    selectors.forEach(function (selector, index) {
      setText(root, selector, valid ? String(counts[index]) : '—');
    });

    const target = root.querySelector('[data-provider-commercial-currencies]');
    if (target) {
      target.replaceChildren();

      if (!valid) {
        target.appendChild(make(
          'span',
          'provider-commercial__empty',
          words('金額資料暫不可用。', 'Value data is unavailable.')
        ));
      } else if (!values.length) {
        target.appendChild(make(
          'span',
          'provider-commercial__empty',
          words('尚未有已獲項目金額。', 'No awarded project value yet.')
        ));
      } else {
        values.forEach(function (item) {
          const currency = clean(item.currency).toUpperCase();
          target.appendChild(make(
            'strong',
            '',
            money(currency, item.awarded_value_minor)
          ));
        });
      }
    }

    return valid;
  }

  function budgetText(rfq) {
    var currency = clean(rfq.currency).toUpperCase();
    var min = minorValue(rfq.budget_min_minor); var max = minorValue(rfq.budget_max_minor);
    if (min !== null && max !== null) return money(currency, min) + ' – ' + money(currency, max);
    if (max !== null) return words('預算上限 ', 'Budget up to ') + money(currency, max);
    if (min !== null) return words('預算起點 ', 'Budget from ') + money(currency, min);
    return words('未提供預算', 'Budget not provided');
  }

  function quoteForm(root, rfq) {
    const form = make('form', 'provider-commercial__quote');
    form.setAttribute('data-provider-quote-form', clean(rfq.rfq_id));
    form.setAttribute('data-currency', clean(rfq.currency).toUpperCase());
    form.setAttribute('aria-busy', 'false');
    var amountWrap = make('div', 'provider-commercial__field'); var amountLabel = make('label', '', words('報價金額（' + clean(rfq.currency).toUpperCase() + '）', 'Quote amount (' + clean(rfq.currency).toUpperCase() + ')'));
    var amount = document.createElement('input'); amount.type = 'text'; amount.inputMode = 'decimal'; amount.autocomplete = 'off'; amount.required = true; amount.maxLength = 24; amount.setAttribute('data-provider-quote-amount', ''); amountLabel.appendChild(amount); amountWrap.appendChild(amountLabel);
    var validWrap = make('div', 'provider-commercial__field'); var validLabel = make('label', '', words('有效至（選填）', 'Valid until (optional)')); var valid = document.createElement('input'); valid.type = 'datetime-local'; valid.setAttribute('data-provider-quote-valid', ''); validLabel.appendChild(valid); validWrap.appendChild(validLabel);
    var noteWrap = make('div', 'provider-commercial__field provider-commercial__field--note'); var noteLabel = make('label', '', words('備註（選填）', 'Note (optional)')); var note = document.createElement('textarea'); note.maxLength = 1000; note.setAttribute('data-provider-quote-note', ''); noteLabel.appendChild(note); noteWrap.appendChild(noteLabel);
    var actions = make('div', 'provider-commercial__actions'); var feedback = make('p', 'provider-commercial__feedback', ''); feedback.setAttribute('data-provider-quote-feedback', ''); feedback.setAttribute('role', 'status'); feedback.setAttribute('aria-live', 'polite'); var button = make('button', 'portal-button portal-button--primary', words('提交報價', 'Submit quote')); button.type = 'submit'; actions.appendChild(feedback); actions.appendChild(button);
    form.appendChild(amountWrap); form.appendChild(validWrap); form.appendChild(noteWrap); form.appendChild(actions); return form;
  }

  function renderRfqs(root, items) {
    var list = root.querySelector('[data-provider-commercial-rfq-list]'); if (!list) return; list.replaceChildren();
    if (!Array.isArray(items) || !items.length) { list.appendChild(make('p', 'provider-commercial__empty', words('目前沒有需要你報價的項目。', 'No RFQs need your quote right now.'))); return; }
    items.forEach(function (rfq) {
      var card = make('article', 'provider-commercial__card'); card.setAttribute('data-provider-rfq-id', clean(rfq.rfq_id)); var copy = make('div', 'provider-commercial__copy'); copy.appendChild(make('strong', '', clean(rfq.title) || words('項目報價', 'Project RFQ')));
      var meta = make('div', 'provider-commercial__meta'); meta.appendChild(make('span', '', budgetText(rfq))); if (clean(rfq.due_at)) meta.appendChild(make('span', '', words('截止 ', 'Due ') + formatDate(rfq.due_at))); meta.appendChild(make('span', '', clean(rfq.status || 'open'))); copy.appendChild(meta);
      var scope = scopeText(rfq.scope); if (scope) copy.appendChild(make('p', 'provider-commercial__scope', scope)); card.appendChild(copy); card.appendChild(quoteForm(root, rfq)); list.appendChild(card);
    });
  }

  async function load(root) {
    const list = root.querySelector('[data-provider-commercial-rfq-list]');
    renderSummary(root, null);

    if (!portalAllowed()) {
      root.setAttribute('data-provider-commercial-state', 'guest');
      setText(
        root,
        '[data-provider-commercial-status]',
        words(
          '登入 GCityPartners 帳戶後可查看 RFQ 與已獲項目。',
          'Sign in to your GCityPartners account to view RFQs and awarded work.'
        )
      );
      if (list) list.replaceChildren();
      return;
    }

    root.setAttribute('data-provider-commercial-state', 'loading');
    setText(
      root,
      '[data-provider-commercial-status]',
      words('正在載入商業資料…', 'Loading commercial data…')
    );
    if (list) {
      list.replaceChildren(make(
        'p',
        'provider-commercial__empty',
        words('正在載入…', 'Loading…')
      ));
    }

    try {
      const results = await Promise.all([
        requestJson(endpoint(root, 'data-provider-rfqs-endpoint')),
        requestJson(endpoint(root, 'data-provider-revenue-endpoint'))
      ]);

      if (!Array.isArray(results[0].items)) {
        throw new Error('invalid_rfq_response');
      }
      if (!renderSummary(root, results[1].summary)) {
        throw new Error('invalid_revenue_summary');
      }

      renderRfqs(root, results[0].items);
      root.setAttribute('data-provider-commercial-state', 'ready');
      setText(root, '[data-provider-commercial-status]', '');
    } catch (error) {
      renderSummary(root, null);
      root.setAttribute('data-provider-commercial-state', 'error');

      const code = clean(error && error.message);
      const message = code === 'provider_not_approved'
        ? words(
            '此帳戶尚未獲准承接 Provider 工作。',
            'This account is not approved for Provider work yet.'
          )
        : words(
            '商業資料暫不可用，未顯示統計數字。',
            'Commercial data is unavailable. Statistics are not displayed.'
          );

      setText(root, '[data-provider-commercial-status]', message);
      if (list) {
        list.replaceChildren(make(
          'p',
          'provider-commercial__empty',
          words('請稍後再試。', 'Try again shortly.')
        ));
      }
    }
  }


  async function submitQuote(root, form) {
    if (form.getAttribute('aria-busy') === 'true'
      || form.getAttribute('data-quote-submit-state') === 'accepted') return;

    const rfqId = clean(form.getAttribute('data-provider-quote-form'));
    const currency = clean(form.getAttribute('data-currency')).toUpperCase();
    const amountField = form.querySelector('[data-provider-quote-amount]');
    const validField = form.querySelector('[data-provider-quote-valid]');
    const noteField = form.querySelector('[data-provider-quote-note]');
    const feedback = form.querySelector('[data-provider-quote-feedback]');
    const button = form.querySelector('button[type="submit"]');

    let requestStarted = false;
    let accepted = false;
    let result;

    try {
      if (!rfqId || !/^[A-Z]{3}$/.test(currency)) {
        throw new Error('invalid_quote_context');
      }

      const amountMinor = majorToMinor(amountField && amountField.value, currency);
      const validUntil = isoFutureFromLocal(validField && validField.value);

      form.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;
      if (feedback) feedback.textContent = words('正在提交…', 'Submitting…');

      requestStarted = true;
      result = await requestJson(
        endpoint(root, 'data-provider-quote-endpoint'),
        {
          method: 'POST',
          body: {
            rfq_id: rfqId,
            currency: currency,
            amount_minor: amountMinor,
            valid_until: validUntil,
            note: clean(noteField && noteField.value) || null
          }
        }
      );

      accepted = true;
      form.setAttribute('data-quote-submit-state', 'accepted');
      if (feedback) {
        feedback.textContent = words(
          '伺服器已接受報價，正在刷新記錄…',
          'The server accepted the quote. Refreshing records…'
        );
      }
    } catch (error) {
      const code = clean(error && error.message);
      const message = code === 'invalid_amount'
        ? words('請輸入有效的報價金額。', 'Enter a valid quote amount.')
        : code === 'invalid_valid_until'
          ? words('報價有效時間必須晚於現在。', 'Quote validity must be later than now.')
          : code === 'invalid_quote_context'
            ? words('RFQ 或幣別資料無效，請重新載入。', 'Invalid RFQ or currency data. Reload to continue.')
            : code === 'rfq_expired'
              ? words('此 RFQ 已截止，不能再提交報價。', 'This RFQ has expired and can no longer accept a quote.')
              : requestStarted
                ? words(
                    '未能確認提交結果。請先重新載入核對記錄，勿立即重複提交。',
                    'The submission result could not be confirmed. Reload and check records before submitting again.'
                  )
                : words('暫時未能提交報價。', 'The quote could not be submitted.');

      if (feedback) feedback.textContent = message;
    } finally {
      form.setAttribute('aria-busy', 'false');
      if (button && document.contains(button)) {
        button.disabled = accepted;
      }
    }

    if (!accepted) return;

    try {
      await load(root);
    } catch (_error) {
      root.setAttribute('data-provider-commercial-state', 'error');
    }

    const refreshed = root.getAttribute('data-provider-commercial-state') === 'ready';
    const message = refreshed
      ? result.idempotent_replay === true
        ? words(
            '伺服器確認相同報價已存在，列表已刷新。',
            'The server confirmed this quote already exists. The list has refreshed.'
          )
        : words(
            '伺服器已接受報價，列表已刷新。',
            'The server accepted the quote. The list has refreshed.'
          )
      : words(
          '伺服器已接受報價，但列表刷新失敗。請重新載入核對，勿重複提交。',
          'The server accepted the quote, but the list could not refresh. Reload to check; do not submit again.'
        );

    setText(root, '[data-provider-commercial-status]', message);
  }

  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    root.addEventListener('submit', function (event) { var form = event.target && event.target.closest ? event.target.closest('[data-provider-quote-form]') : null; if (!form || !root.contains(form)) return; event.preventDefault(); submitQuote(root, form); });
    load(root);
  }

  function mountAll(scope) { Array.prototype.forEach.call((scope || document).querySelectorAll(ROOT_SELECTOR), mount); }
  function refreshAuthorized(scope) { Array.prototype.forEach.call((scope || document).querySelectorAll(ROOT_SELECTOR), function (root) { mount(root); load(root); }); }

  var api = Object.freeze({ version: VERSION, mountAll: mountAll, refresh: refreshAuthorized, majorToMinor: majorToMinor, minorValue: minorValue, isoFutureFromLocal: isoFutureFromLocal });
  window[GLOBAL_KEY] = api; window.GCityProviderCommercial = api;

  document.addEventListener('littleg:web-session-change', function () { refreshAuthorized(document); });
  document.addEventListener('shopify:section:load', function (event) { refreshAuthorized(event.target || document); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mountAll(document); }, { once: true }); else mountAll(document);
})(window, document);